import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30_000; // 30s — light + cheap

/**
 * Returns the live count of pending inquiries (not yet accepted or declined)
 * for the current vendor. Polls every 30s and refreshes on tab focus so the
 * gold blinking badge always reflects reality.
 *
 * Returns 0 for non-vendor accounts.
 */
export function usePendingInquiries() {
  const { getAuthHeaders, user } = useAuth();
  const [count, setCount] = useState(0);
  const timerRef = useRef(null);

  const isVendor = !!user?.roles?.includes('vendor');

  const fetchCount = useCallback(async () => {
    if (!isVendor) {
      setCount(0);
      return;
    }
    try {
      const res = await fetch(`${API}/messages/inquiries`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      const pending = (data.inquiries || []).filter(
        (i) => i.inquiry_meta?.status !== 'accepted' && i.inquiry_meta?.status !== 'declined',
      );
      setCount(pending.length);
    } catch {
      /* swallow — count just stays stale until the next poll */
    }
  }, [getAuthHeaders, isVendor]);

  useEffect(() => {
    if (!isVendor) return undefined;
    fetchCount();
    timerRef.current = setInterval(fetchCount, POLL_MS);
    const onFocus = () => fetchCount();
    window.addEventListener('focus', onFocus);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchCount, isVendor]);

  return { count, refresh: fetchCount };
}
